import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { AuthState_AuthStatus } from '@/shared/api/protocol-ts/api/auth/dto_auth_pb';
import { PATHS } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { markPresignMFAExempt } from '@/shared/lib';
import { resetState } from '@/shared/model';

import { authModel } from '@/entities/auth';
import { userModel } from '@/entities/user';

import { ParsedPresignParams } from '../config';
import {
  clearPresignUrl,
  decidePresignAction,
  getPresignUrl,
  parsePresignParams,
  parseTimestampForProtobuf,
  savePresignUrl,
} from '../lib';

export const usePresignBootstrap = (): void => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const currentUserID = useAppSelector(userModel.selectors.selectCurrentUserID);

  useEffect(() => {
    if (!currentUserID) {
      return;
    }

    const currentParams = parsePresignParams(searchParams);

    const action = decidePresignAction({
      currentParams,
      currentUserID,
      storedUrl: getPresignUrl(),
    });

    const reauthenticate = async (params: ParsedPresignParams) => {
      dispatch(resetState());

      try {
        const response = await dispatch(
          authModel.thunks.signInByPresign({
            Signature: params.required.signature!,
            ClientKeyID: params.required.clientKeyID!,
            SignatureCreationTimestampUnixMilli: parseTimestampForProtobuf(
              params.required.timestampUnixMilli!,
            ),
            UserID: params.required.userID!,
            ...(params.optional.organizationID && {
              OrganizationID: params.optional.organizationID,
            }),
            ...(params.optional.reportID && {
              ReportID: params.optional.reportID,
            }),
          }),
        ).unwrap();

        if (response.Status === AuthState_AuthStatus.SUCCESSFUL) {
          markPresignMFAExempt();
          window.location.reload();
        } else {
          navigate(PATHS.signIn);
        }
      } catch {
        navigate(PATHS.signIn);
      }
    };

    switch (action.type) {
      case 'store': {
        savePresignUrl(window.location.href);
        break;
      }
      case 'reauthenticate': {
        // Drop the stale stored url on write failure, else the reload loops on a mismatch.
        if (!savePresignUrl(window.location.href)) {
          clearPresignUrl();
        }
        void reauthenticate(action.params);
        break;
      }
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserID, searchParams]);
};
